import { getCase } from "../../content/lookups.js";
import { fatigueLimit } from "../../domain/run/fatigueLimit.js";
import { useGame } from "../../app/GameProvider.js";
import { SelectionBanner } from "../../components/SelectionBanner.js";
import { UsedCards } from "../../components/UsedCards.js";
import { DeckStatus } from "../../components/DeckStatus.js";
import { HandList } from "./HandList.js";
import { CaseFoePanel } from "./CaseFoePanel.js";
import { canSubmitInn } from "../../domain/selectors/canSubmitInn.js";
import { fieldJudgmentNotice } from "./fieldJudgmentNotice.js";
import { statHelpOverlay } from "./statHelp.js";
import { hasPlayableAction } from "./hasPlayableAction.js";
import { hasUnselectedOptionalTarget } from "./hasUnselectedOptionalTarget.js";
import { isHandSelection, selectedHandIds } from "./selectionPresentation.js";
import { selectionBannerText } from "../../components/selectionBannerText.js";
import { useCaseStatusDensity } from "./useCaseStatusDensity.js";
import { newlyDealtIds } from "./dealPlan.js";
import { captionSchedule, RESHUFFLE_MS } from "./playCaption.js";
import { useLayoutEffect, useRef, useState } from "react";

export function CasePlayScreen() {
  const { state, dispatch } = useGame();
  const { screenRef, compact } = useCaseStatusDensity();
  const prevHandIdsRef = useRef<readonly string[]>([]);
  const [dealtIds, setDealtIds] = useState<readonly string[]>([]);
  const [caption, setCaption] = useState<string | null>(null);
  const run = state.run;
  const active = run?.activeCase;
  if (!run || !active) return null;

  const caseDef = getCase(active.caseId);
  if (!caseDef) return null;

  const pending = run.pendingChoice ?? null;
  const notice = pending ? null : fieldJudgmentNotice(active);
  const handPending = isHandSelection(pending);
  const overlayPending = Boolean(pending && !handPending);
  const submitEnabled = canSubmitInn(pending, run.hand.length);
  const confirmEndPhase = () => {
    if (hasPlayableAction(run)) {
      dispatch({ type: "OPEN_OVERLAY", overlay: "endPhaseConfirm" });
      return;
    }
    dispatch({ type: "END_PHASE" });
  };
  const submitSelection = () => {
    if (pending && hasUnselectedOptionalTarget(run, pending)) {
      dispatch({ type: "OPEN_OVERLAY", overlay: "skipSelectionConfirm" });
      return;
    }
    dispatch({ type: "SUBMIT_SELECTION" });
  };

  // 新しく手札に現れたカードを山札から配る演出の対象にする。
  // StrictMode の二重レンダーで消えないよう、effect 内で計算して state に載せる。
  const handKey = run.hand.map((card) => card.instanceId).join(",");
  // リシャッフル直後は「混ぜる→山札に戻る」演出が先。配るのはその完了後に合わせる。
  const reshuffled = run.log.at(-1)?.kind === "deck";
  const dealBaseDelayMs = reshuffled ? RESHUFFLE_MS : 0;

  useLayoutEffect(() => {
    const ids = handKey ? handKey.split(",") : [];
    const dealt = newlyDealtIds(prevHandIdsRef.current, ids);
    prevHandIdsRef.current = ids;
    if (dealt.length === 0) return;
    setDealtIds(dealt);
    // クラスを残すと hover の animation:none 解除時に再再生されるため、終了後に外す。
    const clearDealt = window.setTimeout(() => setDealtIds([]), dealBaseDelayMs + dealt.length * 70 + 400);
    // メッセージゾーンに「戻した」「引いた」を演出に合わせて表示する。
    const timers = captionSchedule(reshuffled, dealt.length).map((step) => ({
      show: window.setTimeout(() => setCaption(step.text), step.atMs),
      hide: window.setTimeout(() => setCaption((current) => (current === step.text ? null : current)), step.atMs + step.durationMs),
    }));
    return () => {
      window.clearTimeout(clearDealt);
      for (const timer of timers) {
        window.clearTimeout(timer.show);
        window.clearTimeout(timer.hide);
      }
    };
  }, [handKey, dealBaseDelayMs]);

  return (
    <section ref={screenRef} className={`screen screen-case${compact ? " is-compact" : ""}`}>
      <CaseFoePanel
        name={caseDef.name}
        progress={active.progress}
        pendingProgress={active.pendingProgress}
        required={active.required ?? caseDef.required}
        progressValue={<b>{active.progress}</b>}
        deadline={active.deadline}
        fatigue={run.fatigue}
        fatigueMax={fatigueLimit(run.organizations)}
        block={run.block}
        investments={active.investments}
        onHelp={(topic) => dispatch({ type: "OPEN_OVERLAY", overlay: statHelpOverlay(topic) })}
      />
      {/* 解決画面のメッセージゾーンと同じ帯。空でも高さを予約し、画面間で盤面がずれないようにする。 */}
      <div className="event-caption-slot">
        {caption && <p className="event-caption" role="status" key={caption}>{caption}</p>}
      </div>
      <section className="hand-zone" aria-label="自社の盤面と手札">
        <div className="hand-stack">
          <UsedCards cards={run.inPlay} notice={notice} />
          <HandList run={run} pending={pending} dispatch={dispatch} dealtIds={dealtIds} dealBaseDelayMs={dealBaseDelayMs} />
        </div>
      </section>
      {handPending ? <div className="hand-selection-guide" role="status">{selectionBannerText(pending)}　選択 {selectedHandIds(pending).length} 枚</div> : null}
      {overlayPending ? <SelectionBanner onSubmit={submitSelection} /> : null}
      {!overlayPending ? <footer className="case-actions">
        <div className="case-action-row">
          {pending?.kind === "fieldJudgment" ? (
            <div className="field-judgment-actions" aria-label="現場判断の選択">
              <button type="button" className="btn" onClick={() => dispatch({ type: "OPEN_OVERLAY", overlay: "skipFieldJudgmentConfirm" })}>
                戻す
              </button>
              <button type="button" className="btn btn-primary" onClick={() => dispatch({ type: "TOGGLE_SELECTION", value: "use" })}>
                使用する
              </button>
            </div>
          ) : <>
            <DeckStatus run={run} />
            {pending ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!submitEnabled}
              onClick={submitSelection}
            >
              選択を確定
            </button>
          ) : (
            <button type="button" className="btn btn-primary" onClick={confirmEndPhase}>
              施策フェーズ終了
            </button>
            )}
          </>}
        </div>
      </footer> : null}
    </section>
  );
}
